"use client";

import { useState, useCallback, useEffect, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";

import { Button } from "../../Components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
  DialogHeader,
  DialogFooter,
} from "../../Components/ui/dialog";

import { EditVehiclesDialog } from "../../Components/EditVehiclesDialog";
import { TransferLoadModal } from "./TransferLoadModal";

// Factoring Consent Modal (já existente)
import FactoringConsentModal from "../../Components/FactoringConsentModal";

// Novos módulos do LoadContainer
import { setUserRoleIdOnWindow } from "./LoadContainer/utils/jwt";
import NotesChatModal from "./LoadContainer/modals/NotesChatModal";
import ViewGatePassModal from "./LoadContainer/modals/ViewGatePassModal";
import ModalContent from "./LoadContainer/modals/ModalContent";
import LoadList from "./LoadContainer/components/LoadList";
import LoadDetails from "./LoadContainer/components/LoadDetails";

export default function LoadContainer({
  data,
  meta,
  activeStatus = "All",
  onPrev,
  onNext,
  onRefresh,
  isLoading,
  onOpenAddPhotos,
  onOpenSendDocument,
  onOpenPdfSelection,
  onOpenTransportDetails,
  onOpenConfirmation,
  onOpenOptions,
  onOpenGatePass,
  drivers,
  shippers,
}) {
  const [loads, setLoads] = useState(data);

  useEffect(() => {
    setLoads(data);
  }, [data]);

  const [selectedLoad, setSelectedLoad] = useState(loads[0] || null);

  // Mantém comportamento: quando chega nova lista e não tem seleção, seleciona primeiro
  useEffect(() => {
    if (!selectedLoad && Array.isArray(loads) && loads.length > 0) {
      setSelectedLoad(loads[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loads]);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalAction, setModalAction] = useState("");

  const [isEditVehiclesOpen, setIsEditVehiclesOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

  const [isTransferModalOpen, setIsTransferModalOpen] = useState(false);

  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);

  const [isNotesModalOpen, setIsNotesModalOpen] = useState(false);
  const [isViewGatePassModalOpen, setIsViewGatePassModalOpen] = useState(false);

  const [userRoleId, setUserRoleIdState] = useState(null);

  // Factoring state
  const [isFactoringModalOpen, setIsFactoringModalOpen] = useState(false);
  const [isSubmittingFactoring, setIsSubmittingFactoring] = useState(false);
  const [factoringInfo, setFactoringInfo] = useState(null);

  // ✅ Cancel Factoring state
  const [isCancelFactoringDialogOpen, setIsCancelFactoringDialogOpen] =
    useState(false);
  const [isCancellingFactoring, setIsCancellingFactoring] = useState(false);

  // ✅ refs para controlar scroll corretamente
  const detailsWrapperRef = useRef(null);
  const detailsScrollContainerRef = useRef(null);
  const [detailsScrollRequestKey, setDetailsScrollRequestKey] = useState(0);

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (token) {
      const roleId = setUserRoleIdOnWindow(token);
      setUserRoleIdState(roleId);
    }
  }, []);

  // ✅ Scroll controlado ao trocar load via clique na lista
  useEffect(() => {
    if (!selectedLoad?.id || detailsScrollRequestKey === 0) return;

    let frame1 = null;
    let frame2 = null;

    frame1 = requestAnimationFrame(() => {
      frame2 = requestAnimationFrame(() => {
        const isMobileLayout = window.innerWidth < 1024;

        // Sempre reseta o scroll interno da área de detalhes
        if (
          detailsScrollContainerRef.current &&
          typeof detailsScrollContainerRef.current.scrollTo === "function"
        ) {
          detailsScrollContainerRef.current.scrollTo({
            top: 0,
            behavior: "smooth",
          });
        }

        // No mobile, além do scroll interno, traz o bloco de detalhes para a viewport da página
        if (isMobileLayout && detailsWrapperRef.current) {
          const rect = detailsWrapperRef.current.getBoundingClientRect();
          const absoluteTop = window.scrollY + rect.top;

          window.scrollTo({
            top: Math.max(0, absoluteTop - 8),
            behavior: "smooth",
          });
        }
      });
    });

    return () => {
      if (frame1) cancelAnimationFrame(frame1);
      if (frame2) cancelAnimationFrame(frame2);
    };
  }, [selectedLoad?.id, detailsScrollRequestKey]);

  // Fetch Factoring Info when selectedLoad changes
  useEffect(() => {
    const checkFactoring = async () => {
      if (!selectedLoad?.id) {
        setFactoringInfo(null);
        return;
      }
      try {
        const token = localStorage.getItem("token");
        const response = await axios.get(
          `https://new.cajuu.app/api/factoring/load/${selectedLoad.id}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Screen-Name": "/Load",
            },
          }
        );
        setFactoringInfo(response.data.data || null);
      } catch (error) {
        console.error("Error checking factoring", error);
        setFactoringInfo(null);
      }
    };

    checkFactoring();
  }, [selectedLoad?.id]);

  const currentStatus =
    selectedLoad?.status ||
    selectedLoad?.load_status?.load_status_name ||
    "";

  const isDroppedOrLater = [
    "Dropped Off",
    "Delivered",
    "Billed",
    "Paid",
    "Cancelled",
    "Archived",
  ].includes(currentStatus);

  const showBOLButton = ["Confirmed", "Picked Up", "Dropped Off", "Delivered", "Billed", "Paid"].includes(
    currentStatus
  );

  const getNextStatus = (status) => {
    const statusSequence = {
      New: "Assigned",
      Assigned: "Confirmed",
      Confirmed: "Picked Up",
      "Picked Up": "Dropped Off",
      "Dropped Off": "Delivered",
      Delivered: "Billed",
      Billed: "Paid",
    };
    return statusSequence[status] || null;
  };

  const showStatusButton =
    getNextStatus(currentStatus) !== null && currentStatus !== "Paid";

  const selectedDriverId =
    selectedLoad?.driver_id != null ? String(selectedLoad.driver_id) : "";

  const selectedDriverObj =
    selectedDriverId && Array.isArray(drivers)
      ? drivers.find((d) => String(d.id) === selectedDriverId)
      : null;

  const selectedDriverName =
    selectedDriverObj?.user?.first_name && selectedDriverObj?.user?.last_name
      ? `${selectedDriverObj.user.first_name} ${selectedDriverObj.user.last_name}`
      : selectedLoad?.driver
      ? selectedLoad.driver
      : "No driver assigned";

  const selectedDriverAvatar = selectedDriverObj?.user?.profile_picture_path
    ? `https://new.cajuu.app/storage/${selectedDriverObj.user.profile_picture_path}`
    : "https://new.cajuu.app/storage/profile_pictures/profile_picture.png";

  const handleStatusUpdate = useCallback(async () => {
    if (!selectedLoad) return;

    const nextStatus = getNextStatus(currentStatus);
    if (!nextStatus) {
      alert("Não há próximo status disponível para este load.");
      return;
    }

    if (nextStatus === "Assigned" && !selectedLoad.driver_id) {
      toast.error("You need to assign a driver before changing status to 'Assigned'");
      return;
    }

    setIsUpdatingStatus(true);
    try {
      const allStatusOptions = [
        { id: 1, name: "New" },
        { id: 2, name: "Assigned" },
        { id: 3, name: "Confirmed" },
        { id: 4, name: "Picked Up" },
        { id: 5, name: "Dropped Off" },
        { id: 6, name: "Delivered" },
        { id: 7, name: "Billed" },
        { id: 8, name: "Paid" },
        { id: 9, name: "Cancelled" },
        { id: 10, name: "Archived" },
      ];

      const statusId = allStatusOptions.find((s) => s.name === nextStatus)?.id;
      if (!statusId) {
        alert("Status inválido");
        return;
      }

      const formData = { load_status_id: statusId };
      const userToken = localStorage.getItem("token");

      await axios.post(
        `https://new.cajuu.app/api/loadtransport/lifecycle_alternative/${selectedLoad.id}`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${userToken}`,
            "Content-Type": "multipart/form-data",
            "Screen-Name": "/NewLoad",
          },
        }
      );

      const updatedLoad = {
        ...selectedLoad,
        load_status_id: statusId,
        status: nextStatus,
      };

      updateLoad(updatedLoad);
      toast.success(`Status atualizado para ${nextStatus}`);
    } catch (error) {
      console.error("Error updating status", error);
      toast.error("Erro ao atualizar status");
    } finally {
      setIsUpdatingStatus(false);
    }
  }, [selectedLoad, currentStatus]);

  const handleConfirmDelete = useCallback(async () => {
    if (!selectedLoad?.id) return;

    const id = Number(selectedLoad.id);
    console.log("Deleting load id:", id);

    try {
      const token = localStorage.getItem("token");
      const { data: resp } = await axios.delete(
        `https://new.cajuu.app/api/loadtransport/${id}`,
        {
          headers: {
            "Screen-Name": "/NewLoad",
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          data: { id },
        }
      );

      toast.success(
        typeof resp.data === "string"
          ? resp.data
          : typeof resp.message === "string"
          ? resp.message
          : "Load Transport deleted successfully"
      );

      setLoads((prev) => prev.filter((l) => l.id !== id));
      setSelectedLoad(null);
    } catch (err) {
      console.error("Error deleting load:", err.response ?? err);

      const status = err.response?.status;
      const body = err.response?.data || {};

      const serverMsg =
        typeof body.data === "string"
          ? body.data
          : typeof body.message === "string"
          ? body.message
          : JSON.stringify(body);

      toast.error(`Delete failed${status ? ` (HTTP ${status})` : ""}: ${serverMsg}`);
    } finally {
      setIsDeleteDialogOpen(false);
    }
  }, [selectedLoad]);

  const handleLoadClick = useCallback((load) => {
    setSelectedLoad(load);
    setDetailsScrollRequestKey((prev) => prev + 1);
  }, []);

  const handleMenuItemClick = useCallback((action) => {
    setModalAction(action);
    setIsModalOpen(true);
  }, []);

  const closeModal = useCallback(() => {
    setIsModalOpen(false);
  }, []);

  const handleEditVehicles = () => {
    setIsEditVehiclesOpen(true);
  };

  const handleSaveVehicles = (updatedVehicles) => {
    const updatedLoad = { ...selectedLoad, vehicles: updatedVehicles };
    setSelectedLoad(updatedLoad);
    updateLoad(updatedLoad);
  };

  const updateLoad = (updatedLoad) => {
    const updatedLoads = loads.map((load) =>
      load.id === updatedLoad.id ? updatedLoad : load
    );
    setLoads(updatedLoads);
    setSelectedLoad(updatedLoad);
  };

  const updateAllLoads = (loadId, updates) => {
    setLoads((prevLoads) =>
      prevLoads.map((load) => (load.id === loadId ? { ...load, ...updates } : load))
    );

    if (Array.isArray(data)) {
      for (let i = 0; i < data.length; i++) {
        if (data[i].id === loadId) {
          Object.assign(data[i], updates);
          break;
        }
      }
    }
  };

  const handleOpenBOL = (bolPath) => {
    if (bolPath) {
      window.open(`https://new.cajuu.app/storage/${bolPath}`, "_blank");
    } else {
      toast.error("BOL path is not available");
    }
  };

  const handleOpenInvoice = (invoicePath) => {
    if (invoicePath) {
      window.open(`https://new.cajuu.app/storage/${invoicePath}`, "_blank");
    } else {
      toast.error("Invoice path is not available");
    }
  };

  const handleActionClick = (action, load) => {
    if (!load) return;

    switch (action) {
      case "Add Photo":
        onOpenAddPhotos(load);
        break;
      case "Send BOL by Email":
        onOpenSendDocument(load);
        break;
      case "Gatepass":
        onOpenGatePass(load);
        break;
      case "View PDFs":
        onOpenOptions(load.id);
        break;
      case "Upload PDF":
        onOpenPdfSelection(load);
        break;
      default:
        handleMenuItemClick(action);
        break;
    }
  };

  const hasGatePasses = useCallback(() => {
    if (!selectedLoad) return false;

    const items = selectedLoad.load_items || selectedLoad.vehicles || [];

    return items.some(
      (item) =>
        item.gate_passes &&
        Array.isArray(item.gate_passes) &&
        item.gate_passes.length > 0
    );
  }, [selectedLoad]);

  const handleRequestFactoring = async () => {
    setIsSubmittingFactoring(true);
    try {
      const userToken = localStorage.getItem("token");
      const payload = {
        load_transport_id: selectedLoad.id,
        driver_id: selectedLoad.driver_id,
        partner_company_id: selectedLoad.partner_company_id,
        status: "pending",
      };

      const response = await axios.post(`https://new.cajuu.app/api/factoring`, payload, {
        headers: {
          Authorization: `Bearer ${userToken}`,
          "Content-Type": "application/json",
          "Screen-Name": "/Load",
        },
      });

      const newFactoring = response.data.data || { status: "pending" };
      setFactoringInfo(newFactoring);

      toast.success("Factoring requested successfully!");
      setIsFactoringModalOpen(false);
    } catch (error) {
      console.error("Error requesting factoring", error);
      toast.error("Failed to request factoring");
    } finally {
      setIsSubmittingFactoring(false);
    }
  };

  // ✅ Cancel factoring handler
  const handleCancelFactoring = async () => {
    if (!selectedLoad?.id) return;

    const factoringId = factoringInfo?.id;
    if (!factoringId) {
      toast.error("Factoring request not found for this load.");
      return;
    }

    setIsCancellingFactoring(true);
    try {
      const userToken = localStorage.getItem("token");

      const response = await axios.post(
        `https://new.cajuu.app/api/factoring/${factoringId}/cancel`,
        {},
        {
          headers: {
            Authorization: `Bearer ${userToken}`,
            "Content-Type": "application/json",
            "Screen-Name": "/Load",
          },
        }
      );

      const updatedFactoring =
        response.data?.data || { ...(factoringInfo || {}), status: "cancelled" };

      setFactoringInfo(updatedFactoring);

      toast.success("Factoring request cancelled successfully!");
    } catch (error) {
      console.error("Error cancelling factoring", error);
      toast.error("Failed to cancel factoring request");
    } finally {
      setIsCancellingFactoring(false);
      setIsCancelFactoringDialogOpen(false);
    }
  };

  // Factoring Setup
  const eligiblePaymentTypes = ["COD", "2 BD", "5 BD", "7 BD", "15 BD", "30 BD"];
  const isPaymentEligible = eligiblePaymentTypes.includes(selectedLoad?.payment_type);

  const selectedDriverForFactoring = drivers?.find(
    (d) => String(d.id) === String(selectedLoad?.driver_id)
  );

  const isDriverFactoringEnabled = selectedDriverForFactoring?.is_factoring_enabled == 1;

  const canRequestFactoring =
    isPaymentEligible && isDriverFactoringEnabled && [2, 4].includes(userRoleId);

  // ✅ Can cancel factoring (somente quando PENDING)
  const factoringStatusRaw = (factoringInfo?.status || "").toString().toLowerCase();
  const canCancelFactoring =
    !!factoringInfo?.id &&
    factoringStatusRaw === "pending" &&
    [1, 2, 4, 7].includes(userRoleId);

  const originalRate = parseFloat(selectedLoad?.payment_rate || 0);
  const discountedRate = originalRate * 0.95;

  return (
    <div className="flex flex-col-reverse lg:flex-row w-full h-full gap-4 sm:gap-6">
      <div ref={detailsWrapperRef} className="flex-1 min-h-0">
        <LoadDetails
          scrollContainerRef={detailsScrollContainerRef}
          selectedLoad={selectedLoad}
          userRoleId={userRoleId}
          drivers={drivers}
          shippers={shippers}
          currentStatus={currentStatus}
          isDroppedOrLater={isDroppedOrLater}
          showStatusButton={showStatusButton}
          showBOLButton={showBOLButton}
          selectedDriverName={selectedDriverName}
          selectedDriverAvatar={selectedDriverAvatar}
          onOpenModalAction={handleMenuItemClick}
          onActionClick={handleActionClick}
          onOpenTransferModal={() => setIsTransferModalOpen(true)}
          onOpenDeleteDialog={() => setIsDeleteDialogOpen(true)}
          onOpenNotesModal={() => setIsNotesModalOpen(true)}
          onOpenViewGatePassModal={() => setIsViewGatePassModalOpen(true)}
          hasGatePasses={hasGatePasses}
          onEditVehicles={handleEditVehicles}
          onOpenBOL={handleOpenBOL}
          onOpenInvoice={handleOpenInvoice}
          onStatusUpdate={handleStatusUpdate}
          factoringInfo={factoringInfo}
          discountedRate={discountedRate}
          canRequestFactoring={canRequestFactoring}
          onOpenFactoringModal={() => setIsFactoringModalOpen(true)}
          canCancelFactoring={canCancelFactoring}
          onOpenCancelFactoringDialog={() => setIsCancelFactoringDialogOpen(true)}
        />
      </div>

      <LoadList
        loads={loads}
        selectedLoad={selectedLoad}
        onSelectLoad={handleLoadClick}
        activeStatus={activeStatus}
        meta={meta}
        onPrev={onPrev}
        onNext={onNext}
        drivers={drivers}
      />

      {/* MODAIS */}
      <NotesChatModal
        isOpen={isNotesModalOpen}
        onClose={() => setIsNotesModalOpen(false)}
        selectedLoad={selectedLoad}
      />

      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <ModalContent
          action={modalAction}
          onClose={closeModal}
          selectedLoad={selectedLoad}
          onUpdateLoad={updateLoad}
          drivers={drivers}
          shippers={shippers}
          onUpdateAllLoads={updateAllLoads}
        />
      </Dialog>

      {(selectedLoad?.vehicles || selectedLoad?.load_items)?.length > 0 && (
        <EditVehiclesDialog
          isOpen={isEditVehiclesOpen}
          onClose={() => setIsEditVehiclesOpen(false)}
          vehicles={selectedLoad.vehicles || selectedLoad.load_items}
          onSave={handleSaveVehicles}
        />
      )}

      <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Load</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this load? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDeleteDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              disabled={!selectedLoad?.id}
              onClick={handleConfirmDelete}
            >
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <TransferLoadModal
        isOpen={isTransferModalOpen}
        onClose={() => setIsTransferModalOpen(false)}
        load={selectedLoad}
        onTransferRequested={() => {
          // opcional: atualizar UI se desejar
        }}
      />

      {selectedLoad && (
        <ViewGatePassModal
          isOpen={isViewGatePassModalOpen}
          onClose={() => setIsViewGatePassModalOpen(false)}
          loadItems={selectedLoad.load_items || selectedLoad.vehicles || []}
          selectedLoad={selectedLoad}
          userRoleId={userRoleId}
        />
      )}

      {/* ✅ Cancel Factoring Confirmation Dialog */}
      <Dialog
        open={isCancelFactoringDialogOpen}
        onOpenChange={(open) => {
          if (isCancellingFactoring) return;
          setIsCancelFactoringDialogOpen(open);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Cancel factoring request</DialogTitle>
            <DialogDescription>
              Are you sure you want to cancel this factoring request? This action will mark the
              request as cancelled.
            </DialogDescription>
          </DialogHeader>

          <DialogFooter>
            <Button
              variant="outline"
              disabled={isCancellingFactoring}
              onClick={() => setIsCancelFactoringDialogOpen(false)}
            >
              No, keep it
            </Button>
            <Button
              variant="destructive"
              disabled={isCancellingFactoring}
              onClick={handleCancelFactoring}
            >
              {isCancellingFactoring ? "Cancelling..." : "Yes, cancel"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Factoring Consent Modal */}
      <FactoringConsentModal
        isOpen={isFactoringModalOpen}
        onRequestClose={() => setIsFactoringModalOpen(false)}
        paymentRate={selectedLoad?.payment_rate}
        paymentType={selectedLoad?.payment_type}
        feePercent={5}
        currency="USD"
        isSubmitting={isSubmittingFactoring}
        onAccept={handleRequestFactoring}
        onDecline={() => setIsFactoringModalOpen(false)}
      />
    </div>
  );
}